import { Cell, DayNumber, DayPrice } from './CalendarDay.styles';
import type { CalendarDayProps } from './CalendarDay.types';

function formatDayPrice(amount: number, currencyCode: string): string {
  const major = amount / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(major);
  } catch {
    return `${currencyCode} ${major}`;
  }
}

export function CalendarDay({
  day,
  dateString,
  status,
  price,
  currencyCode,
  isCheckIn,
  isCheckOut,
  isInRange,
  isPast,
  isSelectingCheckOut,
  onClick,
}: CalendarDayProps) {
  if (!day || !dateString) {
    return <Cell data-empty disabled aria-hidden />;
  }

  const isSelected = isCheckIn || isCheckOut;
  const isBlocked =
    isPast ||
    status === 'unavailable' ||
    status === 'hotel_closed' ||
    (status === 'check_in_closed' && !isSelectingCheckOut);

  const showPrice =
    !isBlocked &&
    price !== undefined &&
    price > 0 &&
    currencyCode &&
    (status === 'available' || status === 'check_in_closed');

  const handleClick = () => {
    if (!isBlocked && onClick && dateString) {
      onClick(dateString);
    }
  };

  const isCheckInClosed =
    status === 'check_in_closed' && !isSelectingCheckOut && !isSelected && !isInRange;

  return (
    <Cell
      {...(isBlocked ? { 'data-blocked': '' } : undefined)}
      {...(isSelected ? { 'data-selected': '' } : undefined)}
      {...(isInRange && !isSelected ? { 'data-in-range': '' } : undefined)}
      {...(isCheckInClosed ? { 'data-check-in-closed': '' } : undefined)}
      {...(!isBlocked && !isSelected && day ? { 'data-hoverable': '' } : undefined)}
      onClick={handleClick}
      disabled={isBlocked}
      aria-label={`${dateString}${isCheckIn ? ', check-in' : ''}${isCheckOut ? ', check-out' : ''}`}
      type="button"
    >
      <DayNumber $selected={isSelected}>{day}</DayNumber>
      {showPrice && price !== undefined && currencyCode && (
        <DayPrice $selected={isSelected}>{formatDayPrice(price, currencyCode)}</DayPrice>
      )}
    </Cell>
  );
}
